import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ImageOff, RefreshCw, Search, ExternalLink, Copy, Check, ChevronDown, ChevronUp, Upload, Replace, Undo2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ statuses }) => {
  const isLive = statuses.includes('live');
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase
      ${isLive ? 'bg-red-100 text-red-700' : 'bg-neutral-100 text-neutral-600'}`}>
      {isLive ? 'Live' : 'Draft'}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  const labels = { builder: 'Builder Page', seo: 'SEO', product_data: 'Coded Product', coded_default: 'Coded Default', gallery: 'Gallery', gallery_db: 'Gallery DB' };
  const isOverridable = source === 'product_data' || source === 'coded_default' || source === 'gallery';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isOverridable ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
      {labels[source] || source}{isOverridable ? ' (override)' : ''}
    </span>
  );
};

const ImageThumb = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className="w-12 h-12 rounded bg-neutral-100 flex items-center justify-center"><ImageOff className="w-5 h-5 text-neutral-300" /></div>;
  return <img src={url} alt="" className="w-12 h-12 rounded object-cover border border-neutral-200" onError={() => setFailed(true)} />;
};

/* ── Image Picker Dialog ── */
const ImagePickerDialog = ({ open, onClose, onSelect, token }) => {
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('category', 'gallery');
      const res = await fetch(`${API}/api/admin/gallery/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const imageUrl = data.url || data.image_url || data.file_url;
      if (imageUrl) {
        onSelect(imageUrl);
        onClose();
        toast.success('Image uploaded and selected');
      }
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (url.trim()) {
      onSelect(url.trim());
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5" onClick={e => e.stopPropagation()} data-testid="image-picker-dialog">
        <h3 className="text-base font-semibold text-neutral-900 mb-4">Replace Image</h3>

        {/* Upload */}
        <div className="mb-4">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
          <Button
            variant="outline"
            className="w-full justify-center"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-image-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload New Image'}
          </Button>
        </div>

        {/* Or use URL */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-px bg-neutral-200" />
          <span className="text-xs text-neutral-400">or paste URL</span>
          <div className="flex-1 h-px bg-neutral-200" />
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://..."
            className="flex-1 text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
            data-testid="image-url-input"
          />
          <Button size="sm" onClick={handleUrlSubmit} disabled={!url.trim()} data-testid="use-url-btn">
            Use
          </Button>
        </div>

        <div className="flex justify-end mt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

/* ── Occurrence Row with Replace button ── */
const OccurrenceRow = ({ occ, onReplace }) => (
  <div className="flex items-center gap-2 text-xs py-2 border-b border-neutral-50 last:border-0">
    <SourceBadge source={occ.source} />
    <span className="font-medium text-neutral-700 truncate max-w-[100px]">{occ.page_name}</span>
    <code className="text-neutral-400 bg-neutral-50 px-1 rounded text-[10px] truncate max-w-[80px]">{occ.field}</code>
    <div className="ml-auto flex items-center gap-1.5">
      {occ.page_url && (
        <a href={occ.page_url} target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-neutral-600">
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
      <button
        onClick={() => onReplace(occ)}
        className="text-amber-600 hover:text-amber-700 flex items-center gap-1 text-[10px] font-medium bg-amber-50 px-1.5 py-0.5 rounded"
        data-testid={`replace-btn-${occ.source}`}
      >
        <Replace className="w-3 h-3" /> Fix
      </button>
    </div>
  </div>
);

/* ── Audit Card ── */
const AuditCard = ({ item, onReplace }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyUrl = () => {
    navigator.clipboard.writeText(item.image_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="border border-neutral-100 rounded-lg p-3 hover:border-neutral-200 transition-colors" data-testid="audit-item">
      <div className="flex gap-3">
        <ImageThumb url={item.image_url} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge statuses={item.status} />
            <span className="text-xs text-neutral-400">{item.count} occurrence{item.count > 1 ? 's' : ''}</span>
          </div>
          <p className="text-xs text-neutral-500 truncate mb-0.5" title={item.reason}>{item.reason}</p>
          <div className="flex items-center gap-1.5">
            <code className="text-[10px] text-neutral-400 truncate max-w-[200px] md:max-w-[400px]">{item.image_url}</code>
            <button onClick={copyUrl} className="text-neutral-400 hover:text-neutral-600 flex-shrink-0">
              {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="self-start text-neutral-400 hover:text-neutral-600 p-1">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-2">
            Used in {item.count} location{item.count > 1 ? 's' : ''} — click Fix to replace at each source:
          </p>
          {item.occurrences.map((occ, i) => (
            <OccurrenceRow key={i} occ={occ} onReplace={(o) => onReplace(item, o)} />
          ))}
        </div>
      )}
    </div>
  );
};


/* ════════════════════════════════════════════════════════════════════ */
export const StockImageAudit = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [replacing, setReplacing] = useState(null); // { item, occurrence }

  const runAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/stock-audit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const d = await res.json();
      setData(d);
      toast.success(`Found ${d.unique_stock_images} unique stock images across ${d.total_stock_images} locations`);
    } catch (e) {
      setError(e.message);
      toast.error('Audit failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleReplace = (item, occurrence) => {
    setReplacing({ item, occurrence });
    setPickerOpen(true);
  };

  const handleImageSelected = async (newUrl) => {
    if (!replacing) return;
    const { item, occurrence } = replacing;
    try {
      const res = await fetch(`${API}/api/admin/stock-audit/replace`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: occurrence.source,
          new_url: newUrl,
          old_url: item.image_url,
          page_url: occurrence.page_url,
          field: occurrence.field,
          section_id: occurrence.section?.match(/\(([^)]+)\)/)?.[1] || '',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Replace failed');
      }
      const result = await res.json();
      toast.success(`Replaced via ${result.method}. Re-scan to verify.`);
    } catch (e) {
      toast.error(e.message);
    }
    setReplacing(null);
  };

  const filtered = data?.grouped?.filter(item => {
    if (filter === 'live' && !item.status.includes('live')) return false;
    if (filter === 'draft' && !item.status.includes('draft')) return false;
    if (search) {
      const q = search.toLowerCase();
      return item.image_url.toLowerCase().includes(q)
        || item.reason.toLowerCase().includes(q)
        || item.occurrences.some(o => o.page_name.toLowerCase().includes(q));
    }
    return true;
  }) || [];

  return (
    <Card data-testid="stock-image-audit">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ImageOff className="w-4 h-4 text-amber-600" />
            Stock / Placeholder Image Audit
          </CardTitle>
          <Button size="sm" variant="outline" onClick={runAudit} disabled={loading} data-testid="run-audit-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Scanning...' : data ? 'Re-scan' : 'Run Audit'}
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">{error}</div>}

        {!data && !loading && (
          <div className="text-center py-8 text-neutral-400">
            <ImageOff className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Run the audit to scan all pages for stock and placeholder images</p>
          </div>
        )}

        {data && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-neutral-50 rounded-lg px-3 py-2 text-center">
                <p className="text-lg font-bold text-neutral-900">{data.unique_stock_images}</p>
                <p className="text-[10px] text-neutral-500 uppercase">Unique Images</p>
              </div>
              <div className="bg-neutral-50 rounded-lg px-3 py-2 text-center">
                <p className="text-lg font-bold text-neutral-900">{data.total_stock_images}</p>
                <p className="text-[10px] text-neutral-500 uppercase">Total Locations</p>
              </div>
              <div className="bg-neutral-50 rounded-lg px-3 py-2 text-center">
                <p className="text-lg font-bold text-red-600">
                  {data.grouped?.filter(g => g.status.includes('live')).length || 0}
                </p>
                <p className="text-[10px] text-neutral-500 uppercase">On Live Pages</p>
              </div>
            </div>

            {/* Info */}
            <div className="flex items-start gap-2 mb-4 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <p>Expand an image and click <strong>Fix</strong> on each location to replace it. Builder and gallery images update directly. Coded defaults use a temporary override (shown with an amber badge).</p>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search by page, URL, or reason..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="audit-search"
                />
              </div>
              <div className="flex gap-1">
                {['all', 'live', 'draft'].map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-2.5 py-1 text-xs rounded-full transition-all capitalize
                      ${filter === f ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
                    data-testid={`filter-${f}`}
                  >
                    {f === 'all' ? `All (${data.unique_stock_images})` : f}
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1" data-testid="audit-results">
              {filtered.length === 0 ? (
                <p className="text-center text-sm text-neutral-400 py-4">No matching stock images found</p>
              ) : (
                filtered.map((item, i) => <AuditCard key={i} item={item} onReplace={handleReplace} />)
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Image picker dialog */}
      <ImagePickerDialog
        open={pickerOpen}
        onClose={() => { setPickerOpen(false); setReplacing(null); }}
        onSelect={handleImageSelected}
        token={token}
      />
    </Card>
  );
};
